// Morpheus API client — talks to the Auth Gateway (146), which injects the
// server-side key and forwards to the brain (234). Auth via Bearer token.
import { getToken, logout } from "./auth";

const MORPHEUS_API_URL = "https://api.morpheus.maatz.studio";

function authHeaders(extra = {}) {
  const t = getToken();
  return { ...extra, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

async function handle(res) {
  if (res.status === 401) {
    logout();
    throw new Error("Session abgelaufen – bitte neu einloggen.");
  }
  if (!res.ok) {
    throw new Error(`Morpheus API error: ${res.status}`);
  }
  return res.json();
}

export const morpheus = {
  async run(text, thread = null, namespace = "prod", agent = null) {
    const res = await fetch(`${MORPHEUS_API_URL}/agent/run`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(agent ? { say: text, namespace, thread, agent } : { say: text, namespace, thread }),
    });
    return handle(res);
  },

  // SPIDER (Relationship-Agent) direkt — liefert text + optional drafts[]
  async runSpider(text, thread) {
    return this.run(text, thread, "prod", "spider");
  },

  // KOBOLD (Finanz-Agent) direkt — liefert text (keine drafts)
  async runKobold(text, thread) {
    return this.run(text, thread, "prod", "kobold");
  },

  // BOOKER (Booking-Agent) direkt — liefert text + optional drafts[] (Email-Entwuerfe)
  async runBooker(text, thread) {
    return this.run(text, thread, "prod", "booker");
  },

  // HERALD (Promotion / Spotify-Wizard) direkt — liefert text + optional drafts[] (Promo-Outreach)
  async runHerald(text, thread) {
    return this.run(text, thread, "prod", "herald");
  },
  async getReleases(status = "", brand = "") {
    const qs = [status && `status=${encodeURIComponent(status)}`, brand && `brand=${encodeURIComponent(brand)}`].filter(Boolean).join("&");
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/releases${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async addRelease(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/releases`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async updateRelease(id, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/releases/${id}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async getIdeas(status = "", tag = "") {
    const qs = [status && `status=${encodeURIComponent(status)}`, tag && `tag=${encodeURIComponent(tag)}`].filter(Boolean).join("&");
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/ideas${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async addIdea(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/ideas`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async updateIdea(id, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/ideas/${id}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async getAdCreatives(status = "", releaseId = "") {
    const qs = [status && `status=${encodeURIComponent(status)}`, releaseId && `release_id=${encodeURIComponent(releaseId)}`].filter(Boolean).join("&");
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/ad-creatives${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async addAdCreative(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/ad-creatives`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async updateAdCreative(id, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/ad-creatives/${id}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  // Herald (25.09.2026): Karriere ehrlich, Social-Signale je Video/Song, letzter Wochenbericht.
  async getPromoKarriere() {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/karriere`, { headers: authHeaders() });
    return handle(res);
  },
  async getPromoSignale(tage = 30, fresh = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/signale?tage=${tage}${fresh ? "&fresh=1" : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getPromoBericht() {
    const res = await fetch(`${MORPHEUS_API_URL}/promotion/bericht`, { headers: authHeaders() });
    return handle(res);
  },
  async getBookingEvents(status = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/booking/events${status ? `?status=${status}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getBookingBookers(status = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/booking/bookers${status ? `?status=${status}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getBookingConversations() {
    const res = await fetch(`${MORPHEUS_API_URL}/booking/conversations`, { headers: authHeaders() });
    return handle(res);
  },
  // Booker-Seite fertig (02.11.): Mail-Verlauf eines Balls und Vorschaubilder aus dem Pressfolder.
  async getBookingThread(account, id) {
    const res = await fetch(`${MORPHEUS_API_URL}/booking/thread?account=${encodeURIComponent(account)}&id=${encodeURIComponent(id)}`, { headers: authHeaders() });
    return handle(res);
  },
  bookingVorschauUrl(pfad) {
    return `${MORPHEUS_API_URL}/booking/material/vorschau?pfad=${encodeURIComponent(pfad)}&token=${encodeURIComponent(getToken() || "")}`;
  },
  async getBookingMaterial() {
    const res = await fetch(`${MORPHEUS_API_URL}/booking/material`, { headers: authHeaders() });
    return handle(res);
  },
  async getBookingBoard(fresh = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/booking/board${fresh ? "?fresh=1" : ""}`, { headers: authHeaders() });
    return handle(res);
  },

  // MORPHEUS Agenda-Tab. Endpoint erst nach Brain-Restart live; AgendaPanel degradiert bis dahin.
  // fresh=true kostet 1 LLM-Call → nur fuer den expliziten Refresh-Button.
  async getAgenda(fresh = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/agenda${fresh ? "?fresh=1" : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async agendaVeto(id) {
    const res = await fetch(`${MORPHEUS_API_URL}/agenda/veto`, {
      method: "POST", headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ id }),
    });
    return handle(res);
  },
  // Flotten-Status für die Konstellation (60s-Cache serverseitig, kein LLM); fresh nach Loop-Toggle
  async getFleet(fresh = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/fleet${fresh ? "?fresh=1" : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getCrons(fresh = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/crons${fresh ? "?fresh=1" : ""}`, { headers: authHeaders() });
    return handle(res);
  },

  async resume(thread, namespace = "prod") {
    const res = await fetch(`${MORPHEUS_API_URL}/agent/run`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ say: "", namespace, thread }),
    });
    return handle(res);
  },

  async listConversations(agent = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/conversations${agent ? `?agent=${agent}` : ""}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },

  async getConversation(conversationId, namespace = "prod") {
    const res = await fetch(`${MORPHEUS_API_URL}/conversations/${conversationId}?namespace=${namespace}`, {
      headers: authHeaders(),
    });
    return handle(res);
  },

  async health() {
    const res = await fetch(`${MORPHEUS_API_URL}/health`);
    return res.json();
  },

  async getOverview() {
    const res = await fetch(`${MORPHEUS_API_URL}/overview`, { headers: authHeaders() });
    return handle(res);
  },
  async getFinancials(tab = "2026") {
    const res = await fetch(`${MORPHEUS_API_URL}/financials?tab=${tab}`, { headers: authHeaders() });
    return handle(res);
  },
  async getLoops() {
    const res = await fetch(`${MORPHEUS_API_URL}/loops`, { headers: authHeaders() });
    return handle(res);
  },
  async toggleLoop(name, on) {
    const res = await fetch(`${MORPHEUS_API_URL}/loops/toggle`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ name, on }) });
    return handle(res);
  },
  async getTasks(status = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/tasks${status ? `?status=${status}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async completeTask(taskId, notes = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/tasks/${taskId}/complete`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ notes }) });
    return handle(res);
  },
  async runSocial(input, campaignId = "web-kreieren") {
    const res = await fetch(`${MORPHEUS_API_URL}/social/run`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ input, campaign_id: campaignId }) });
    return handle(res);
  },
  async getContentOverview() {
    const res = await fetch(`${MORPHEUS_API_URL}/social/overview`, { headers: authHeaders() });
    return handle(res);
  },
  async getContentCalendar(days = 14) {
    const res = await fetch(`${MORPHEUS_API_URL}/social/calendar?days=${days}`, { headers: authHeaders() });
    return handle(res);
  },
  async scheduleDraft(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/social/schedule-draft`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async getReels() {
    const res = await fetch(`${MORPHEUS_API_URL}/content/reels`, { headers: authHeaders() });
    return handle(res);
  },
  async getDropbox(path = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/dropbox?path=${encodeURIComponent(path)}`, { headers: authHeaders() });
    return handle(res);
  },
  async getDropboxLink(path) {
    const res = await fetch(`${MORPHEUS_API_URL}/dropbox/link?path=${encodeURIComponent(path)}`, { headers: authHeaders() });
    return handle(res);
  },
  // `marke` (29.08.2026): dieselbe Datei kann einen anderen Film enthalten — ein
  // Render unter demselben Pfad. Ohne Marke bleibt die URL gleich, das <video>
  // wird nicht angefasst und der Browser zeigt weiter seine geladene Fassung
  // (Mattis „zeigt wieder nicht im player"). Der Server ignoriert den Parameter;
  // er ist nur fuer den Browser da. Ohne Marke bleibt die URL wie bisher — sonst
  // wuerde jeder Kartenwechsel unnoetig neu laden.
  mediaUrl(path, marke) {
    const basis = `${MORPHEUS_API_URL}/dropbox/stream?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken() || "")}`;
    return marke ? `${basis}&frisch=${encodeURIComponent(marke)}` : basis;
  },
  async postContentDecision(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/content/decision`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async getBrandBible() {
    const res = await fetch(`${MORPHEUS_API_URL}/brand/bible`, { headers: authHeaders() });
    return handle(res);
  },
  async getBrandDoc(path) {
    const res = await fetch(`${MORPHEUS_API_URL}/brand/doc?path=${encodeURIComponent(path)}`, { headers: authHeaders() });
    return handle(res);
  },
  async getCutterStatus() {
    const res = await fetch(`${MORPHEUS_API_URL}/social/cutter-status`, { headers: authHeaders() });
    return handle(res);
  },
  async refreshBank() {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/bank/refresh`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }) });
    return handle(res);
  },
  async getKoboldCashflow(month = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/cashflow${month ? `?month=${month}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getPlanOverview() {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/plan_overview`, { headers: authHeaders() });
    return handle(res);
  },
  async getSpendingPlan(month = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/spending_plan${month ? `?month=${month}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getKoboldIncome() {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/income`, { headers: authHeaders() });
    return handle(res);
  },
  async getAgentCosts() {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/agent_costs`, { headers: authHeaders() });
    return handle(res);
  },
  async getKoboldSpaceTx(account, month = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/transactions?account=${encodeURIComponent(account)}&include_internal=1&limit=10${month ? `&month=${month}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getStreams() {
    const res = await fetch(`${MORPHEUS_API_URL}/streams`, { headers: authHeaders() });
    return handle(res);
  },
  async getPerformance() {
    const res = await fetch(`${MORPHEUS_API_URL}/performance`, { headers: authHeaders() });
    return handle(res);
  },
  async getAgents() {
    const res = await fetch(`${MORPHEUS_API_URL}/agents`, { headers: authHeaders() });
    return handle(res);
  },
  async getFigures() {
    const res = await fetch(`${MORPHEUS_API_URL}/figures`, { headers: authHeaders() });
    return handle(res);
  },
  async getLexware() {
    const res = await fetch(`${MORPHEUS_API_URL}/lexware`, { headers: authHeaders() });
    return handle(res);
  },
  async getKoboldContracts() {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/contracts`, { headers: authHeaders() });
    return handle(res);
  },
  // Ohne &template= entscheidet der Vertrag selbst, in welchem Wortlaut er gilt
  // (04.08.2026). Vorher hing hier master_agreement_v1_en fest im Query-String und
  // haette beim naechsten Vertrag mit anderem Wortlaut das Falsche gezeigt.
  contractPreviewUrl(contractId) {
    return `${MORPHEUS_API_URL}/kobold/contracts/preview?contract_id=${encodeURIComponent(contractId)}&token=${encodeURIComponent(getToken() || "")}`;
  },
  contractPdfUrl(path) {
    return `${MORPHEUS_API_URL}/kobold/contracts/pdf?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken() || "")}`;
  },
  async moveContractFile(path, category) {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/contracts/move`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ path, category }) });
    return handle(res);
  },
  async getFaden(contactId) {
    const res = await fetch(`${MORPHEUS_API_URL}/contacts/${encodeURIComponent(contactId)}/faden`, { headers: authHeaders() });
    return handle(res);
  },
  async getContacts() {
    const res = await fetch(`${MORPHEUS_API_URL}/contacts`, { headers: authHeaders() });
    return handle(res);
  },
  async getRepertoire(q = "", catalog = "all") {
    const qs = [q && `q=${encodeURIComponent(q)}`, catalog && `catalog=${catalog}`].filter(Boolean).join("&");
    const res = await fetch(`${MORPHEUS_API_URL}/spider/repertoire${qs ? `?${qs}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async getSongMatches(songId, catalog = "sony", topK = 8) {
    const res = await fetch(`${MORPHEUS_API_URL}/spider/song_matches?song_id=${encodeURIComponent(songId)}&catalog=${catalog}&top_k=${topK}`, { headers: authHeaders() });
    return handle(res);
  },
  async getCalendar(days = 45, mode = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/calendar?days=${days}${mode ? `&mode=${mode}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  // Entwuerfe-Reiter (26.08.): dieselben Entwuerfe, die im Telegram mit zwei Knoepfen
  // liegen — hier mit Textfeld, weil man an einer Verhandlungsmail arbeiten will.
  async getOutreachDrafts(agent = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/outreach/drafts${agent ? `?agent=${encodeURIComponent(agent)}` : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  // gesendeterText: was Matti WIRKLICH rausgeschickt hat (26.08.). Ohne ihn wurde nur
  // gezaehlt, DASS er etwas geaendert hat, nie was — daraus liess sich nichts lernen.
  // Der Mail-Verlauf zu einem Entwurf (26.08.). Matti: "ich will da was abschreiben."
  async getDraftThread(draftId) {
    const res = await fetch(`${MORPHEUS_API_URL}/outreach/draft_thread?draft_id=${encodeURIComponent(draftId)}`, { headers: authHeaders() });
    return handle(res);
  },
  async setDraftStatus(draftId, status, gesendeterText = "") {
    const res = await fetch(`${MORPHEUS_API_URL}/outreach/draft_status`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ draft_id: draftId, status, gesendeter_text: gesendeterText }) });
    return handle(res);
  },
  // Rechnungen auf demselben Schreibtisch (03.09.2026). Matti: "wir haben ja einen
  // schreibtisch mit den entwuerfen, das sollte ja auch so sein." Eine geparkte Rechnung
  // erreichte ihn bis dahin nur ueber Telegram — scrollt er vorbei, haengt sie fest,
  // und zwar final in Lexware, waehrend der Kunde nichts davon weiss.
  // ⚠️ Rechnungen laufen NICHT ueber sendOutreach. Das baut die Mail aus dem Entwurfs-
  // TEXT, eine Rechnung hat aber keinen Text sondern ein PDF — es ginge eine LEERE Mail
  // an den Kunden. Deshalb eigener Topf auf dem Brain und eigene Knoepfe hier.
  async getRechnungen() {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/rechnungen`, { headers: authHeaders() });
    return handle(res);
  },
  // aktion: "senden" | "verwerfen". Kein Default — "senden" darf nie aus Versehen passieren.
  async setRechnungFreigabe(draftId, aktion) {
    const res = await fetch(`${MORPHEUS_API_URL}/kobold/rechnungen/freigabe`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ draft_id: draftId, aktion }) });
    return handle(res);
  },
  // Token in der URL wie beim Vertrags-PDF: ein <a>/<iframe> kann keine Header setzen.
  rechnungPdfUrl(draftId) {
    return `${MORPHEUS_API_URL}/kobold/rechnungen/pdf?draft_id=${encodeURIComponent(draftId)}&token=${encodeURIComponent(getToken() || "")}`;
  },
  async sendOutreach(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/outreach/send`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async getInbox(accounts = "matti,maatz,funkenstein", q = "in:inbox", limit = 12) {
    const res = await fetch(`${MORPHEUS_API_URL}/inbox?accounts=${encodeURIComponent(accounts)}&q=${encodeURIComponent(q)}&limit=${limit}`, { headers: authHeaders() });
    return handle(res);
  },
  async getInboxThread(account, id) {
    const res = await fetch(`${MORPHEUS_API_URL}/inbox/thread?account=${encodeURIComponent(account)}&id=${encodeURIComponent(id)}`, { headers: authHeaders() });
    return handle(res);
  },
  async getLeads() {
    const res = await fetch(`${MORPHEUS_API_URL}/leads`, { headers: authHeaders() });
    return handle(res);
  },

  // ---- VERBINDEN "Am Ball" ----
  async getBalls(fresh = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/balls${fresh ? "?fresh=1" : ""}`, { headers: authHeaders() });
    return handle(res);
  },
  async patchLead(id, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/leads/${id}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async postInteraction(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/interactions`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async createContact(payload) {
    const res = await fetch(`${MORPHEUS_API_URL}/contacts`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
    return handle(res);
  },
  async patchContact(id, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/contacts/${id}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async triageSuggest() {
    const res = await fetch(`${MORPHEUS_API_URL}/leads/triage-suggest`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: "{}" });
    return handle(res);
  },
  // Drag&Drop-Overrides (Ball → Bereich/Genre); Endpoint erst nach Brain-Restart live,
  // AmBall fällt bis dahin auf localStorage zurück.
  async getUiAmball() {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/amball`, { headers: authHeaders() });
    return handle(res);
  },
  async setUiAmball(key, area, sub = null) {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/amball`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ key, area, sub }) });
    return handle(res);
  },
  async getConnectors() {
    const res = await fetch(`${MORPHEUS_API_URL}/connectors`, { headers: authHeaders() });
    return handle(res);
  },
  async getTopology() {
    const res = await fetch(`${MORPHEUS_API_URL}/topology`, { headers: authHeaders() });
    return handle(res);
  },
  async getTools() {
    const res = await fetch(`${MORPHEUS_API_URL}/tools`, { headers: authHeaders() });
    return handle(res);
  },
  async getContentTools() {
    const res = await fetch(`${MORPHEUS_API_URL}/social/tools`, { headers: authHeaders() });
    return handle(res);
  },
  async runEinstein(message) {
    const res = await fetch(`${MORPHEUS_API_URL}/einstein/chat`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ message, namespace: "prod", style: "concise", tools_enabled: true, max_rounds: 5 }),
    });
    return handle(res);
  },
  async getEinsteinMemory() {
    const res = await fetch(`${MORPHEUS_API_URL}/einstein-memory`, { headers: authHeaders() });
    return handle(res);
  },
  async getProjectHealth() {
    const res = await fetch(`${MORPHEUS_API_URL}/project-health`, { headers: authHeaders() });
    return handle(res);
  },
  async getGoalProgress(id) {
    const res = await fetch(`${MORPHEUS_API_URL}/goals/${encodeURIComponent(id)}/progress`, { headers: authHeaders() });
    return handle(res);
  },
  async getDecisions(dueReview = false) {
    const res = await fetch(`${MORPHEUS_API_URL}/decisions?due_review=${dueReview ? "true" : "false"}`, { headers: authHeaders() });
    return handle(res);
  },
  async getGoals() {
    const res = await fetch(`${MORPHEUS_API_URL}/goals`, { headers: authHeaders() });
    return handle(res);
  },
  // RICHTUNG: Ziel-Baum (CEO-Top-Goals → Agent-Goals) mit hochgerolltem Fortschritt.
  async getRichtung() {
    const res = await fetch(`${MORPHEUS_API_URL}/richtung`, { headers: authHeaders() });
    return handle(res);
  },
  async createGoal(fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/goals`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async updateGoal(goalId, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/goals/${encodeURIComponent(goalId)}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async getUiProjects() {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/projects`, { headers: authHeaders() });
    return handle(res);
  },
  async createUiProject(name) {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/projects`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ name }) });
    return handle(res);
  },
  async patchUiProject(pid, fields) {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/projects/${pid}`, { method: "PATCH", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(fields) });
    return handle(res);
  },
  async deleteUiProject(pid) {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/projects/${pid}`, { method: "DELETE", headers: authHeaders() });
    return handle(res);
  },
  async assignThread(threadId, projectId) {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/projects/assign`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ thread_id: threadId, project_id: projectId }) });
    return handle(res);
  },
  async getProjectContext(pid) {
    const res = await fetch(`${MORPHEUS_API_URL}/ui/projects/${pid}/context`, { headers: authHeaders() });
    return handle(res);
  },

  // Das Büro des Projekt-CEO (23.09.2026): Projekte = aktive Ziele, der Tisch, Los.
  async getProjekte() {
    const res = await fetch(`${MORPHEUS_API_URL}/projekte`, { headers: authHeaders() });
    return handle(res);
  },
  async getTisch(projektId) {
    const q = projektId ? `?projekt=${encodeURIComponent(projektId)}` : "";
    const res = await fetch(`${MORPHEUS_API_URL}/tisch${q}`, { headers: authHeaders() });
    return handle(res);
  },
  async projektLos(goalId) {
    const res = await fetch(`${MORPHEUS_API_URL}/projekte/${encodeURIComponent(goalId)}/los`, { method: "POST", headers: authHeaders() });
    return handle(res);
  },
  // Vorschlag statt Verteilen (25.09.2026): Los oder Weg fuer einen einzelnen Vorschlag.
  async vorschlagLos(taskId) {
    const res = await fetch(`${MORPHEUS_API_URL}/auftraege/${encodeURIComponent(taskId)}/los`, { method: "POST", headers: authHeaders() });
    return handle(res);
  },
  async vorschlagWeg(taskId, satz) {
    const res = await fetch(`${MORPHEUS_API_URL}/auftraege/${encodeURIComponent(taskId)}/weg`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(satz ? { satz } : {}),
    });
    return handle(res);
  },
  async getProjectBrief(goalId) {
    const res = await fetch(`${MORPHEUS_API_URL}/project/${encodeURIComponent(goalId)}/brief`, { headers: authHeaders() });
    return handle(res);
  },

  // Modell-Umschalter (03.09.2026). Gleiche Form wie kreieren.getLlm/setLlm, damit
  // die Einstellungen-Karte fuer Cutter und Morpheus identisch aussieht.
  async getLlm() {
    const res = await fetch(`${MORPHEUS_API_URL}/morpheus/llm`, { headers: authHeaders() });
    return handle(res);
  },
  // agent (04.09.2026): "kobold" | "spider" | "herald" | "booker" pinnt nur diesen
  // Agenten; key "" loest den Pin (folgt Morpheus). Ohne agent = globaler Schalter.
  async setLlm(key, agent) {
    const res = await fetch(`${MORPHEUS_API_URL}/morpheus/llm`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(agent ? { key, agent } : { key }),
    });
    return handle(res);
  },
};
